/**
 * Captures uncaught errors and unhandled promise rejections in the browser
 * and sends them to the API error-log endpoint with optional user context.
 * Requires auth: sends the session bearer token (Cognito id token by default,
 * or whatever setRestAuthTokenProvider() supplies) so the endpoint can accept the request.
 * Call initBrowserErrorReporting() at app bootstrap and setErrorReportUserContext()
 * when the user is known (e.g. after auth).
 * Use ErrorReportingBoundary at the app root to also report React component-tree errors.
 */

import React from 'react';
import { getRestAuthToken } from './rest-auth-token';

const ERROR_LOG_PATH = '/api/errors';

let apiBaseUrl: string;
let appName: string;
let currentUserId: string | null = null;
let initialized = false;

export interface LogErrorPayload {
  app: string;
  message: string;
  level?: string;
  stack?: string;
  code?: string;
  userId?: string;
  url?: string;
  metadata?: string;
  environment?: string;
  occurredAt?: string;
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 3) + '...';
}

function sendToApi(payload: LogErrorPayload): void {
  if (!initialized || !apiBaseUrl) return;
  const url = `${apiBaseUrl}${ERROR_LOG_PATH}`;
  const body = {
    app: payload.app,
    message: truncate(payload.message, 4000),
    level: payload.level ?? 'error',
    stack: payload.stack ? truncate(payload.stack, 4000) : undefined,
    code: payload.code,
    userId: payload.userId ? truncate(payload.userId, 100) : undefined,
    url: payload.url ? truncate(payload.url, 500) : undefined,
    metadata: payload.metadata ? truncate(payload.metadata, 4000) : undefined,
    environment: payload.environment,
    occurredAt: payload.occurredAt ?? new Date().toISOString()
  };
  getRestAuthToken()
    .then((token) => {
      if (!token) return;
      return fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body),
        keepalive: true
      });
    })
    .catch(() => {
      /* ignore send failure */
    });
}

function getPayloadFromError(message: string, source?: string, stack?: string, error?: unknown): LogErrorPayload {
  let stackStr = stack;
  let code: string | undefined;
  if (error instanceof Error) {
    stackStr = stackStr ?? error.stack;
    code = error.name !== 'Error' ? error.name : undefined;
  }
  return {
    app: appName,
    message,
    level: 'error',
    stack: stackStr,
    code,
    userId: currentUserId ?? undefined,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    environment:
      typeof process !== 'undefined' && process.env ? (process.env['NODE_ENV'] as string | undefined) : undefined,
    occurredAt: new Date().toISOString()
  };
}

function onError(event: ErrorEvent): void {
  const { message, filename, lineno, colno, error } = event;
  const detail = [filename, lineno, colno].filter(Boolean).join(':');
  const fullMessage = detail ? `${message} (${detail})` : message;
  const payload = getPayloadFromError(
    fullMessage,
    'window.onerror',
    error instanceof Error ? error.stack : undefined,
    error
  );
  sendToApi(payload);
}

function onUnhandledRejection(event: PromiseRejectionEvent): void {
  const reason = event.reason;
  const message = reason instanceof Error ? reason.message : String(reason);
  const stack = reason instanceof Error ? reason.stack : undefined;
  const payload = getPayloadFromError(message, 'unhandledrejection', stack, reason);
  sendToApi(payload);
}

/**
 * Call once at app bootstrap (e.g. in main.tsx).
 * apiBaseUrl should be the API root (e.g. environment.baseApiUrl) without trailing slash.
 */
export function initBrowserErrorReporting(config: { apiBaseUrl: string; appName: string }): void {
  if (initialized) return;
  apiBaseUrl = config.apiBaseUrl.replace(/\/$/, '');
  appName = config.appName;
  initialized = true;

  if (typeof window === 'undefined') return;

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onUnhandledRejection);
}

/**
 * Set the current user identifier (e.g. email or username) so it is included
 * on subsequent error reports. Call when the user logs in and clear (undefined)
 * on logout. Can be called from a component that has access to auth (e.g. useAuthenticator).
 */
export function setErrorReportUserContext(userId: string | undefined): void {
  currentUserId = userId ?? null;
}

/**
 * Report a React Error Boundary catch to the API. Used internally by ErrorReportingBoundary.
 */
export function reportErrorFromErrorBoundary(error: Error, componentStack: string): void {
  if (!initialized) return;
  const payload = getPayloadFromError(error.message, 'ErrorBoundary', error.stack, error);
  payload.metadata = componentStack;
  sendToApi(payload);
}

interface ErrorReportingBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface ErrorReportingBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Root Error Boundary that reports React component-tree errors to the same
 * /api/errors endpoint (with userId when set). Wrap your app (e.g. inside
 * Authenticator) so render-phase errors are captured and sent.
 */
export class ErrorReportingBoundary extends React.Component<ErrorReportingBoundaryProps, ErrorReportingBoundaryState> {
  override state: ErrorReportingBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorReportingBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    reportErrorFromErrorBoundary(error, errorInfo.componentStack ?? '');
  }

  override render(): React.ReactNode {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) return this.props.fallback;
      const err = this.state.error;
      return React.createElement(
        'div',
        {
          style: {
            padding: 24,
            fontFamily: 'sans-serif',
            maxWidth: 560,
            margin: '40px auto'
          }
        },
        React.createElement('h2', { style: { marginTop: 0 } }, 'Something went wrong'),
        React.createElement('p', null, 'This error has been reported. Please try refreshing the page.'),
        React.createElement(
          'details',
          { style: { whiteSpace: 'pre-wrap', fontSize: 12, marginTop: 16 } },
          err.message,
          '\n\n',
          err.stack ?? ''
        )
      );
    }
    return this.props.children;
  }
}
